// File: src/pages/AppRoutes.tsx
//
// Canonical SPA routing for SynkTribe.
// - AppShell wraps all child routes (header, ticker, drawers).
// - Enterprise: dedicated shell + guarded nested routes.
// - Admin: platform-wide console (claims.admin).
// - This router is the single source of truth.

import React, { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useRouteError,
} from "react-router-dom";

import AppShell from "@/layouts/AppShell";
import BottomTabShell from "@/layouts/product/BottomTabShell";
import OmniCineEditorShell from "@/layouts/product/OmniCineEditorShell";

// Admin (platform)
import AdminDashboard from "@/admin/AdminDashboard";

// Enterprise / Gov
import EnterpriseRoutes from "@/enterprise/EnterpriseRoutes";
import GovLogin from "@/auth/GovLogin";
import { ProtectedRoute } from "@/auth/Protected";
import OpsDashboardRoute from "@/routes/OpsDashboardRoute";

// Smart entry helpers
import { useUser } from "@/hooks/useUser";
import { prefetchOmniCineRoute } from "@/lib/routePrefetch";
import {
  SOURCE_MATERIALS_READBACK_PREVIEW_ROUTE_PATH,
  SourceMaterialsReadbackPreviewRoute,
} from "@/features/guidedStudio/materialStaging/ui/SourceMaterialsReadbackPreviewRoute";
import {
  COMMAND_DECK_LIVE_ROUTE_SEGMENT,
  COMMAND_DECK_PREVIEW_ROUTE_SEGMENT,
} from "@/features/commandDeck/config/commandDeckMvp";
import { CommandDeckLiveRoute } from "@/features/commandDeck/CommandDeckLiveRoute";
import { CommandDeckPreviewRoute } from "@/features/commandDeck/CommandDeckPreviewRoute";

// Optional legacy workspace (kept if still referenced)
import GoalBuddyWorkspace from "@/pages/goalbuddy/GoalBuddyWorkspace";

/* ---------------------------------------------
 * Inline fallback UI for lazy pages
 * --------------------------------------------- */
function GlassLoader() {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      style={{
        padding: 16,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 12,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        lineHeight: 1.3,
        color: "rgba(255,255,255,0.88)",
      }}
    >
      <span aria-hidden>⏳</span>
      <span>Loading SynkTribe…</span>
    </div>
  );
}

function RouteErrorBoundary() {
  const err = useRouteError() as { message?: string } | undefined;
  return (
    <div style={{ padding: 24, color: "white" }}>
      <h2 style={{ fontSize: 18, marginBottom: 8, fontWeight: 600 }}>
        Something went sideways.
      </h2>
      <p style={{ fontSize: 14, lineHeight: 1.45, opacity: 0.85 }}>
        Try navigating back or refreshing. If it keeps happening, open the
        console and grab a screenshot for Auralis.
      </p>
      {err?.message && (
        <pre
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 8,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            fontSize: 12,
            overflow: "auto",
          }}
        >
          {String(err.message)}
        </pre>
      )}
    </div>
  );
}

const lazyEl = (Node: React.ReactNode) => (
  <Suspense fallback={<GlassLoader />}>{Node}</Suspense>
);

const sourceMaterialsReadbackPreviewRoutePath =
  SOURCE_MATERIALS_READBACK_PREVIEW_ROUTE_PATH ===
  "/guided-studio/source-materials-readback-preview"
    ? "guided-studio/source-materials-readback-preview"
    : SOURCE_MATERIALS_READBACK_PREVIEW_ROUTE_PATH;

/* ---------------------------------------------
 * Lazy pages (alias-based, extensionless)
 * --------------------------------------------- */

// Public / landing
const Landing = lazy(() => import("@/pages/Landing"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const AccountPage = lazy(() => import("@/pages/Account"));
const EnterpriseLogin = lazy(() => import("@/auth/EnterpriseLogin"));

// GoalBuddy canonical page
const GoalBuddyPage = lazy(() => import("@/pages/GoalBuddyPage"));
// Guest Landing / flows
const GoalBuddyLanding = lazy(() => import("@/pages/goalbuddy/GoalBuddyLanding"));
const GoalBuddyGuest = lazy(() => import("@/pages/goalbuddy/GoalBuddyGuest"));
const GoalBuddyOnboarding = lazy(
  () => import("@/pages/goalbuddy/GoalBuddyOnboarding")
);

// Media / Binaural / Readiness
const MediaDashboard = lazy(() => import("@/pages/MediaDashboard"));
const MediaHubPage = lazy(() => import("@/pages/MediaHubPage"));
const BinauralBeatsDashboard = lazy(
  () => import("@/components/binaural/BinauralBeatsDashboard")
);
const NeuralReadinessDashboard = lazy(
  () => import("@/pages/NeuralReadinessDashboard")
);

// Omni surfaces
const OmniCast = lazy(() => import("@/pages/OmniCast"));
const OmniCine = lazy(prefetchOmniCineRoute);
const OmniStudio = lazy(() => import("@/pages/OmniStudio"));
const SynkStrive = lazy(() => import("@/pages/SynkStrive"));
const GuidedStudio = lazy(() => import("@/pages/GuidedStudio"));
const GuidedWorldStart = lazy(() => import("@/pages/GuidedWorldStart"));
const GuidedStartFromWorldRoute = lazy(() => import("@/routes/guided/GuidedStartFromWorldRoute"));
const WalletPage = lazy(() => import("@/pages/wallet/WalletPage"));
const NFTSKTPage = lazy(() => import("@/pages/NFTSKT"));
const SmartSkinsPage = lazy(() => import("@/pages/SmartSkins"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const ControlledAccessHoldingPage = lazy(
  () => import("@/features/auth/ControlledAccessHoldingPage")
);
const PathwayAccessShellPage = lazy(
  () => import("@/features/auth/PathwayAccessShellPage")
);
const WearablesEntryPage = lazy(() => import("@/pages/Wearables/WearablesEntryPage"));
const WearablesHubPage = lazy(() => import("@/pages/Wearables/WearablesHubPage"));
const WearablesOnboardingPage = lazy(
  () => import("@/pages/Onboarding/WearablesOnboardingPage")
);

// Settings
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const DevicesSettingsPage = lazy(() => import("@/pages/Settings/Devices"));

// Auth / callbacks
// IMPORTANT: folder is `auth` (lowercase) on disk → must match for Linux builds
const SpotifyCallback = lazy(() => import("@/pages/auth/SpotifyCallback"));

/** Legacy Admin console (kept under /admin/console) */
const AdminDashboardLayout = lazy(
  () => import("@/layouts/AdminDashboardLayout")
);
const AdminHome = lazy(() => import("@/pages/admin/AdminHome"));
const Devices = lazy(() => import("@/pages/admin/Devices"));
const Events = lazy(() => import("@/pages/admin/Events"));
const Ledger = lazy(() => import("@/pages/admin/Ledger"));
const ESLintPanel = lazy(() => import("@/pages/admin/ESLintPanel"));
const MemoryControl = lazy(() => import("@/pages/admin/MemoryControl"));
const UIRegistry = lazy(() => import("@/pages/admin/UIRegistry"));
const TickerAdmin = lazy(() => import("@/pages/admin/TickerAdmin"));
const CoachAccessPanel = lazy(() => import("@/pages/admin/CoachAccessPanel"));
const AuralisDevHealth = lazy(() => import("@/pages/admin/AuralisDevHealth"));

// Experimental / previews
const NextGenHeroPreview = lazy(() => import("@/components/NextGenHeroPreview"));

// Guest / green room / replay
const JoinSession = lazy(() => import("@/pages/JoinSession"));
const GreenRoom = lazy(() => import("@/pages/GreenRoom"));
const ReplaySession = lazy(() => import("@/pages/ReplaySession"));

// Coaches / AI Mentors
const CoachesMentorsPage = lazy(() => import("@/pages/CoachesMentors"));
const MentorDetail = lazy(() => import("@/views/coaches/MentorDetail"));

/* ---------------------------------------------
 * GoalBuddy smart entry + legacy redirect
 * --------------------------------------------- */

// Signed-in → `/goalbuddy/app` (+ preserve querystring) ; guest → landing.
function SmartGoalBuddyEntry() {
  const { user } = useUser();
  const loc = useLocation();
  const search = loc.search || "";
  return user ? (
    <Navigate to={`/goalbuddy/app${search}`} replace />
  ) : (
    lazyEl(<GoalBuddyLanding />)
  );
}

// Legacy `/goalbuddy/start` → redirect based on auth (+ preserve querystring).
function RedirectGoalBuddyStart() {
  const { user } = useUser();
  const loc = useLocation();
  const search = loc.search || "";
  return (
    <Navigate to={`${user ? "/goalbuddy/app" : "/goalbuddy"}${search}`} replace />
  );
}

/* ---------------------------------------------
 * Router definition
 * --------------------------------------------- */
export const router = createBrowserRouter(
  [
    // Top-level enterprise & gov auth entries (outside AppShell)
    { path: "/enterprise/login", element: <EnterpriseLogin /> },
    { path: "/gov/login", element: <GovLogin /> },
    { path: "/ops", element: <OpsDashboardRoute /> },

    // Enterprise tenant routes (own shell/guards inside)
    { path: "/enterprise/*", element: <EnterpriseRoutes /> },

    // Main app shell + child routes
    {
      path: "/",
      element: <AppShell />,
      errorElement: <RouteErrorBoundary />,
      children: [
        // Account
        { path: "account", element: <AccountPage /> },

        {
          element: <BottomTabShell />,
          children: [
            // Landing / home
            { index: true, element: <Navigate to="/access" replace /> },
            { path: "landing-vnext", element: lazyEl(<Landing />) },
            { path: "home-legacy", element: lazyEl(<HomePage />) },

            /* ---------------------------------
             * GoalBuddy (canonical)
             * --------------------------------- */
            { path: "goalbuddy", element: <SmartGoalBuddyEntry /> },
            { path: "goalbuddy/start", element: <RedirectGoalBuddyStart /> },
            { path: "goalbuddy/guest", element: lazyEl(<GoalBuddyGuest />) },
            { path: "goalbuddy/onboarding", element: lazyEl(<GoalBuddyOnboarding />) },

            // Authed app: render GoalBuddyPage after sign-in
            { path: "goalbuddy/app", element: lazyEl(<GoalBuddyPage />) },

            // (optional) legacy workspace
            { path: "goalbuddy/workspace", element: lazyEl(<GoalBuddyWorkspace />) },

            // Omni product surfaces
            { path: "omnicast", element: lazyEl(<OmniCast />) },
            { path: "omnicast/:sessionId", element: lazyEl(<OmniCast />) },
            { path: "omnicast/s/:sessionId/r/:snapshotRef", element: lazyEl(<OmniCast />) },
            { path: "xr", element: lazyEl(<GuidedWorldStart />) },
            { path: "guided/start-from-world", element: lazyEl(<GuidedStartFromWorldRoute />) },
            { path: "omnistudio", element: lazyEl(<OmniStudio />) },
            { path: "synkstrive", element: lazyEl(<SynkStrive />) },
            { path: "wallet", element: lazyEl(<WalletPage />) },
            { path: "nft", element: lazyEl(<NFTSKTPage />) },
            { path: "smartskins", element: lazyEl(<SmartSkinsPage />) },
            { path: "smart-skins", element: <Navigate to="/smartskins" replace /> },
            { path: "smart-tattoos", element: <Navigate to="/smartskins" replace /> },
            { path: "wearables/start", element: lazyEl(<WearablesEntryPage />) },
            {
              path: "onboarding/wearables",
              element: lazyEl(
                <ProtectedRoute>
                  <WearablesOnboardingPage />
                </ProtectedRoute>
              ),
            },
            { path: "wearables/connect", element: lazyEl(<WearablesHubPage />) },
            { path: "coaches", element: lazyEl(<CoachesMentorsPage />) },
          ],
        },

        { path: "guided-studio", element: lazyEl(<GuidedStudio />) },
        ...(import.meta.env.DEV
          ? [
              {
                path: sourceMaterialsReadbackPreviewRoutePath,
                element: <SourceMaterialsReadbackPreviewRoute />,
              },
            ]
          : []),
        {
          path: COMMAND_DECK_LIVE_ROUTE_SEGMENT,
          element: <CommandDeckLiveRoute />,
        },
        {
          path: COMMAND_DECK_PREVIEW_ROUTE_SEGMENT,
          element: <CommandDeckPreviewRoute />,
        },

        {
          element: <OmniCineEditorShell />,
          children: [{ path: "omnicine", element: lazyEl(<OmniCine />) }],
        },

        // Legacy Explore paths now collapse back to landing.
        { path: "explore", element: <Navigate to="/" replace /> },
        { path: "explore-legacy", element: <Navigate to="/" replace /> },
        { path: "login", element: lazyEl(<AuthPage />) },
        { path: "access", element: lazyEl(<ControlledAccessHoldingPage />) },
        { path: "access/pathway", element: lazyEl(<PathwayAccessShellPage />) },
        { path: "nextgen", element: lazyEl(<NextGenHeroPreview />) },

        // OAuth / callbacks
        { path: "spotify/callback", element: lazyEl(<SpotifyCallback />) },
        
        // Media / Binaural / Readiness
        { path: "media", element: lazyEl(<MediaDashboard />) },
        { path: "media-hub", element: lazyEl(<MediaHubPage />) },
        { path: "binaural", element: lazyEl(<BinauralBeatsDashboard />) },
        { path: "dash/binaural", element: lazyEl(<BinauralBeatsDashboard />) },
        { path: "readiness", element: lazyEl(<NeuralReadinessDashboard />) },

        { path: "coaches/:id", element: lazyEl(<MentorDetail />) },

        // Legacy path kept for compatibility → redirect to canonical
        { path: "coaches-mentors", element: <Navigate to="/coaches" replace /> },

        // Guest / co-host
        { path: "join", element: lazyEl(<JoinSession />) },
        { path: "greenroom/:sessionId", element: lazyEl(<GreenRoom />) },
        { path: "replay/:sessionId", element: lazyEl(<ReplaySession />) },

        // Settings
        { path: "settings", element: lazyEl(<SettingsPage />) },
        { path: "settings/devices", element: lazyEl(<DevicesSettingsPage />) },

        // NEW Admin dashboard (platform)
        { path: "admin/auralis/dev-health", element: lazyEl(<AuralisDevHealth />) },
        { path: "admin", element: <AdminDashboard /> },

        // Legacy Admin (under /admin/console)
        {
          path: "admin/console",
          element: lazyEl(<AdminDashboardLayout />),
          errorElement: <RouteErrorBoundary />,
          children: [
            { index: true, element: lazyEl(<AdminHome />) },
            ...(
              [
                ["devices", Devices],
                ["events", Events],
                ["ledger", Ledger],
                ["eslint", ESLintPanel],
                ["memory", MemoryControl],
                ["ui", UIRegistry],
                ["ticker", TickerAdmin],
                ["coach-access", CoachAccessPanel],
              ] as const
            ).map(([p, Comp]) => ({
              path: p,
              element: lazyEl(<Comp />),
            })) as { path: string; element: React.ReactNode }[],
          ],
        },

        // Fallback
        { path: "*", element: <Navigate to="/access" replace /> },
      ],
    },
  ],
  {
    // Respect Vite base; avoids hard-coded origins that cause bad dynamic-import URLs
    basename:
      (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ||
      "/",
  }
);

export default router;
